import { getVapidPublicKey, subscribeWebPush, unsubscribeWebPush } from "../api/client";

// Web Push subscriptions are keyed by an ArrayBuffer applicationServerKey,
// but the VAPID public key travels as a URL-safe base64 string — this is
// the standard conversion every Web Push integration needs.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export function isWebPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window;
}

// Registers the service worker (idempotent — safe to call every app load)
// and subscribes this browser to push, sending the subscription to the
// backend for the given role. Silently no-ops on unsupported browsers or a
// denied/dismissed permission prompt — notifications are an enhancement,
// never something the rest of the app should block on.
export async function setupWebPush(role) {
  if (!isWebPushSupported()) return;

  try {
    const registration = await navigator.serviceWorker.register("/service-worker.js");

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;

    const { data } = await getVapidPublicKey();
    if (!data?.publicKey) return;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey)
      });
    }

    await subscribeWebPush(role, subscription.toJSON());
  } catch (err) {
    console.warn("[webPush] Setup failed:", err);
  }
}

export async function teardownWebPush(role) {
  if (!isWebPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    await subscription?.unsubscribe();
    await unsubscribeWebPush(role);
  } catch {
    // Best-effort on logout — nothing to recover if this fails.
  }
}
